/**
 * PlanRefreshComparison — Plan-Health-Anzeige + Verbesserungsvorschlag
 *
 * Zeigt für den gewählten Monat:
 *   1. Aktuellen Plan-Health (Score 0–100 + verbales Label)
 *   2. Vorgeschlagene Verbesserung (analytics-optimierter Mix)
 *   3. Visuelle Pillar-Verteilung (vorher vs. nachher, Balkendiagramm)
 *   4. CTA „Vorschlag übernehmen" → schreibt neuen Plan (nur Autopilot-Posts)
 *
 * Design-Entscheidungen:
 * - Kompakter Trigger-Card (immer sichtbar wenn Monat/Jahr gesetzt)
 * - Analyse erst beim Klick laden (kein automatischer schwerer Request)
 * - „Vorschlag übernehmen" kommuniziert klar was passiert
 * - Manuell erstellte Posts bleiben immer erhalten (nur source='autopilot' ersetzt)
 * - Kein leerer Zustand: bei has_existing_plan=false dezenter Hinweis
 */

import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, ChevronDown, ArrowRight, CheckCircle,
  Loader2, BarChart3, AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Farb-Definitionen ─────────────────────────────────────────────────────────

const PILLAR_COLORS = {
  education:  "bg-blue-400",
  trust:      "bg-green-400",
  engagement: "bg-amber-400",
  offer:      "bg-purple-400",
};
const PILLAR_DOT = {
  education:  "bg-blue-400",
  trust:      "bg-green-400",
  engagement: "bg-amber-400",
  offer:      "bg-purple-400",
};
const PILLAR_LABELS = {
  education: "Mehrwert", trust: "Vertrauen", engagement: "Interaktion", offer: "Angebote",
};

const FORMAT_COLORS = {
  post:     "bg-stone-400",
  carousel: "bg-cyan-400",
  video:    "bg-violet-400",
};
const FORMAT_LABELS = {
  post: "Beitrag", carousel: "Karussell", video: "Video",
};

// ── Health-Score Farbe ─────────────────────────────────────────────────────────

const scoreColor = (score) => {
  if (score >= 70) return "text-green-600";
  if (score >= 50) return "text-amber-500";
  return "text-red-500";
};

const scoreBg = (score) => {
  if (score >= 70) return "bg-green-50 border-green-200";
  if (score >= 50) return "bg-amber-50 border-amber-200";
  return "bg-red-50 border-red-200";
};

// ── Hilfsfunktionen ───────────────────────────────────────────────────────────

const pct = (f) => `${Math.round((f || 0) * 100)}%`;

// ── Pillar-Balken ─────────────────────────────────────────────────────────────

const PillarBar = ({ distribution, label }) => {
  const entries = Object.entries(distribution)
    .filter(([, v]) => v > 0)
    .sort(([a], [b]) => {
      const order = ["education", "trust", "engagement", "offer"];
      return order.indexOf(a) - order.indexOf(b);
    });

  return (
    <div>
      {label && (
        <p className="text-xs font-medium text-stone-500 mb-1.5">{label}</p>
      )}
      {/* Gestapelter Balken */}
      <div className="flex rounded-full overflow-hidden h-5 bg-stone-100">
        {entries.map(([pillar, frac]) => (
          <div
            key={pillar}
            style={{ width: pct(frac) }}
            className={`${PILLAR_COLORS[pillar] || "bg-stone-300"} transition-all`}
            title={`${PILLAR_LABELS[pillar] || pillar}: ${pct(frac)}`}
          />
        ))}
      </div>
      {/* Legende */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
        {entries.map(([pillar, frac]) => (
          <span key={pillar} className="flex items-center gap-1 text-xs text-stone-500">
            <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${PILLAR_DOT[pillar] || "bg-stone-400"}`} />
            {PILLAR_LABELS[pillar] || pillar} {pct(frac)}
          </span>
        ))}
      </div>
    </div>
  );
};

// ── Format-Balken ─────────────────────────────────────────────────────────────

const FormatBar = ({ distribution }) => {
  const entries = Object.entries(distribution).filter(([, v]) => v > 0);

  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([fmt, frac]) => (
        <span
          key={fmt}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-white font-medium ${FORMAT_COLORS[fmt] || "bg-stone-400"}`}
        >
          {FORMAT_LABELS[fmt] || fmt} {pct(frac)}
        </span>
      ))}
    </div>
  );
};

// ── Health-Score Karte ────────────────────────────────────────────────────────

const HealthCard = ({ score, label, prefix }) => (
  <div className={`flex flex-col items-center p-3 rounded-xl border ${scoreBg(score)} min-w-[100px]`}>
    <p className="text-xs text-stone-500 mb-1">{prefix}</p>
    <p className={`text-3xl font-bold tabular-nums ${scoreColor(score)}`}>{score}</p>
    <p className="text-xs text-stone-400 mb-1">/100</p>
    <p className="text-xs text-stone-600 text-center leading-tight">{label}</p>
  </div>
);

// ── Haupt-Komponente ──────────────────────────────────────────────────────────

const MONTH_NAMES = [
  "", "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

const PlanRefreshComparison = ({ month, year, platforms = ["instagram", "facebook", "linkedin"], onSuccess }) => {
  const [status, setStatus]               = useState("idle"); // idle | loading | loaded | no_plan | applying | done
  const [data, setData]                   = useState(null);
  const [changesSummary, setChangesSummary] = useState([]);

  const handleAnalyze = async () => {
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/autopilot/refresh-plan`, {
        month,
        year,
        platforms,
      });
      if (res.data.has_existing_plan === false) {
        setStatus("no_plan");
        setData(res.data);
      } else {
        setStatus("loaded");
        setData(res.data);
      }
    } catch (err) {
      toast.error("Der Vorschlag konnte nicht geladen werden");
      setStatus("idle");
    }
  };

  const handleApply = async () => {
    if (!data) return;
    setStatus("applying");
    try {
      const res = await axios.post(`${API}/autopilot/apply-refresh`, {
        month,
        year,
        platforms,
        goals:        data.detected_goals       || ["Sichtbarkeit"],
        intensity:    data.detected_intensity    || "normal",
        schwerpunkte: data.detected_schwerpunkte || [],
      });

      if (res.data.success) {
        setChangesSummary(res.data.change_summary || []);
        setStatus("done");
        toast.success(
          `Plan aktualisiert! ${res.data.imported_count} neue Entwürfe für ${MONTH_NAMES[month]}.`
        );
        onSuccess?.();
      } else {
        toast.error("Plan konnte nicht übernommen werden");
        setStatus("loaded");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Übernahme fehlgeschlagen");
      setStatus("loaded");
    }
  };

  // ── Trigger-Card ──────────────────────────────────────────────────────────

  if (status === "idle") {
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
            <BarChart3 size={17} className="text-stone-500" />
          </div>
          <div>
            <p className="text-sm font-semibold text-stone-700">
              Frische Ideen für {MONTH_NAMES[month]}
            </p>
            <p className="text-xs text-stone-400 mt-0.5">
              Zeigt dir einen alternativen Vorschlag für deinen Monat
            </p>
          </div>
        </div>
        <button
          onClick={handleAnalyze}
          className="flex items-center gap-1.5 px-4 py-2 bg-stone-800 text-white rounded-xl text-sm font-medium hover:bg-stone-700 transition-colors flex-shrink-0"
        >
          <RefreshCw size={13} />
          Vorschlag ansehen
        </button>
      </div>
    );
  }

  // ── Laden ─────────────────────────────────────────────────────────────────

  if (status === "loading") {
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-4 flex items-center gap-3">
        <Loader2 size={18} className="animate-spin text-stone-400" />
        <p className="text-sm text-stone-500">Vorschlag wird erstellt…</p>
      </div>
    );
  }

  // ── Kein Plan ─────────────────────────────────────────────────────────────

  if (status === "no_plan") {
    return (
      <div className="rounded-xl border border-stone-200 bg-stone-50 p-4 flex items-start gap-3">
        <AlertCircle size={16} className="text-stone-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-stone-600">
            Noch kein Plan für {MONTH_NAMES[month]} {year}
          </p>
          <p className="text-xs text-stone-400 mt-0.5">
            Erstelle zuerst einen Plan mit dem Autopilot oben — dann kannst du ihn hier verbessern.
          </p>
        </div>
        <button
          onClick={() => setStatus("idle")}
          className="ml-auto text-xs text-stone-400 hover:text-stone-600"
        >
          ✕
        </button>
      </div>
    );
  }

  // ── Erfolg nach Übernahme ─────────────────────────────────────────────────

  if (status === "done") {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <CheckCircle size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-green-800">
              Plan für {MONTH_NAMES[month]} {year} wurde aktualisiert
            </p>
            <p className="text-xs text-green-600 mt-0.5">
              Der neue Plan ist jetzt im Kalender sichtbar.
            </p>
          </div>
          <button
            onClick={() => { setStatus("idle"); setChangesSummary([]); }}
            className="text-xs text-green-500 hover:text-green-700 flex-shrink-0"
          >
            Nochmals analysieren
          </button>
        </div>

        {/* Was hat sich geändert */}
        {changesSummary.length > 0 && (
          <div className="bg-white rounded-xl border border-green-200 p-4 space-y-2.5">
            <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">
              Was sich an deinem Plan geändert hat
            </p>
            <ul className="space-y-2">
              {changesSummary.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
                  <span className="text-green-500 flex-shrink-0 font-bold">✓</span>
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-stone-400 pt-1 border-t border-green-100 mt-2">
              Diesen Refresh findest du ab jetzt auch in der Autopilot-Historie weiter unten.
            </p>
          </div>
        )}
      </div>
    );
  }

  // ── Vollständige Vergleichsansicht ────────────────────────────────────────

  if ((status === "loaded" || status === "applying") && data) {
    const { current, suggested, score_delta, used_analytics } = data;
    const isImprovement = score_delta > 0;

    return (
      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100 bg-stone-50">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-stone-500" />
            <p className="text-sm font-semibold text-stone-700">
              Plananalyse — {MONTH_NAMES[month]} {year}
            </p>
            {used_analytics && (
              <span className="text-xs text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
                ✨ Mit deinen Daten optimiert
              </span>
            )}
          </div>
          <button
            onClick={() => setStatus("idle")}
            className="text-stone-400 hover:text-stone-600 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Health-Score Vergleich */}
          <div>
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">
              Ausgewogenheit deines Monats
            </p>
            <div className="flex items-center gap-4">
              <HealthCard
                score={current.health.score}
                label={current.health.label}
                prefix="Aktuell"
              />
              <div className="flex flex-col items-center gap-1 flex-shrink-0">
                <ArrowRight
                  size={20}
                  className={isImprovement ? "text-green-500" : "text-stone-300"}
                />
                {isImprovement && (
                  <span className="text-xs font-semibold text-green-600">
                    +{score_delta} Punkte
                  </span>
                )}
              </div>
              <HealthCard
                score={suggested.health.score}
                label={suggested.health.label}
                prefix="Vorschlag"
              />
            </div>
          </div>

          {/* Pillar-Verteilung Vergleich */}
          <div>
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">
              Inhaltsarten im Vergleich
            </p>
            <div className="space-y-3">
              <PillarBar
                distribution={current.pillar_distribution}
                label={`Aktuell (${current.total_posts} Posts)`}
              />
              <PillarBar
                distribution={suggested.pillar_distribution}
                label={`Vorschlag (${suggested.total_posts} Posts)`}
              />
            </div>
          </div>

          {/* Format-Verteilung */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-stone-500 mb-1.5">Formate — Aktuell</p>
              <FormatBar distribution={current.format_distribution} />
            </div>
            <div>
              <p className="text-xs font-medium text-stone-500 mb-1.5">Formate — Vorschlag</p>
              <FormatBar distribution={suggested.format_distribution} />
            </div>
          </div>

          {/* Erklärung */}
          {isImprovement && (
            <p className="text-xs text-stone-500 bg-stone-50 rounded-lg p-3 leading-relaxed">
              <strong className="text-stone-700">Was ändert sich?</strong>{" "}
              Wir aktualisieren deinen Plan für {MONTH_NAMES[month]} mit einem ausgewogeneren Mix —
              nur deine Autopilot-Entwürfe werden ersetzt. Manuell erstellte Posts bleiben erhalten.
              {used_analytics && " Der Vorschlag berücksichtigt, was in den letzten Wochen bei deiner Community gut ankam."}
            </p>
          )}
          {!isImprovement && (
            <p className="text-xs text-stone-500 bg-stone-50 rounded-lg p-3 leading-relaxed">
              Dein Plan ist bereits gut ausgewogen. Ein neuer Vorschlag würde nur minimale Änderungen bringen.
            </p>
          )}

          {/* CTAs */}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleApply}
              disabled={status === "applying"}
              className="flex items-center gap-2 px-4 py-2.5 bg-fox-fire text-white rounded-xl font-semibold text-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === "applying" ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Wird übernommen…
                </>
              ) : (
                <>
                  <CheckCircle size={14} />
                  Vorschlag übernehmen
                </>
              )}
            </button>
            <button
              onClick={() => setStatus("idle")}
              disabled={status === "applying"}
              className="px-4 py-2.5 text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-xl text-sm font-medium transition-colors"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PlanRefreshComparison;
